import ApiServices from '@Src/apiServices';
import { getCompanyId } from '@Src/commons/Utils/authUtils';
import { GenericError } from '@Src/commons/Constants';
import { UploadFile } from 'antd';
import {
  GstinCredentialType,
  TypeBankDetails,
  TypeCompanyDetails,
  TypeRequestOtp,
  TypeRequestOtpResponse,
  TypeStatutoryInformations,
  TypeVerifyOtp,
} from './Type';

export const getCompanyProfile = async (): Promise<TypeCompanyDetails> => {
  const companyId = getCompanyId();
  if (!companyId) {
    throw new Error(GenericError.ERR_USER_DETAILS_DECODE);
  }
  const response = (await ApiServices.getCompanyProfileAPI(
    companyId
  )) as TypeCompanyDetails;
  return response;
};

export const getCompanyVirtualAccounts = async (): Promise<any> => {
  const companyId = getCompanyId();
  if (!companyId) {
    throw new Error(GenericError.ERR_USER_DETAILS_DECODE);
  }

  return ApiServices.getFinanceAccountDetails(companyId);
};

export const getCompanyIdentifier = async (): Promise<TypeCompanyDetails> => {
  const companyId = getCompanyId();
  if (!companyId) {
    throw new Error(GenericError.ERR_USER_DETAILS_DECODE);
  }
  const response = (await ApiServices.getCompanyIdentifierAPI(
    companyId
  )) as TypeCompanyDetails;
  return response;
};
export const getCompanyIdentifierId = async (
  gstin: string
): Promise<TypeCompanyDetails> => {
  if (!gstin) {
    throw new Error(GenericError.ERR_USER_DETAILS_DECODE);
  }
  const response = (await ApiServices.getCompanyIdentifierIdAPI(
    gstin
  )) as TypeCompanyDetails;
  return response;
};

export const getShowBanner = async (
  companyId: number
): Promise<TypeCompanyDetails> => {
  if (!companyId) {
    throw new Error(GenericError.ERR_USER_DETAILS_DECODE);
  }
  const response = (await ApiServices.getShowBannerPrompt(
    companyId
  )) as TypeCompanyDetails;
  return response;
};

export const getGSTRReportsStatus = async (
  getCompanyIdentifierId: number
): Promise<TypeCompanyDetails> => {
  const response = (await ApiServices.getGSTRReports(
    getCompanyIdentifierId
  )) as TypeCompanyDetails;
  return response;
};
export const getPlatformFee = async (companyId: number) => {
  const response = await ApiServices.getShowBannerPlatformFeePrompt(companyId);
  return response;
};

export const getOTP = async (
  data: TypeRequestOtp
): Promise<TypeRequestOtpResponse> => {
  const response = (await ApiServices.getSendOTP(
    data
  )) as TypeRequestOtpResponse;
  return response;
};

export const verifyOTP = async (
  data: TypeVerifyOtp
): Promise<TypeRequestOtpResponse> => {
  const response = (await ApiServices.getVerifyOTP(
    data
  )) as TypeRequestOtpResponse;
  return response;
};
export const getCompanyStatutoryInformations = async () => {
  const companyId = getCompanyId();
  if (!companyId) {
    throw new Error(GenericError.ERR_USER_DETAILS_DECODE);
  }

  const response = (await ApiServices.getCompanyStatutoryInformationsAPI(
    companyId
  )) as TypeStatutoryInformations;
  return response;
};

export const updateBankDetails = async (data: TypeBankDetails) => {
  if (!data.id) throw new Error(GenericError.ERROR);
  const response = (await ApiServices.updateBankDetailsAPI(
    data.id,
    data
  )) as TypeStatutoryInformations;
  return response;
};

export const getCredentialList = async () => {
  const response =
    (await ApiServices.getCredentialListAPI()) as GstinCredentialType[];
  return response;
};

export const addCredential = async (data: GstinCredentialType) => {
  if (!data) throw new Error(GenericError.ERROR);
  const response = (await ApiServices.createCredentialAPI(
    data
  )) as GstinCredentialType[];
  return response;
};

export const updateCredential = async (data: GstinCredentialType) => {
  if (!data) throw new Error(GenericError.ERROR);
  const response = (await ApiServices.updateCredentialAPI(
    data
  )) as GstinCredentialType;
  return response;
};

export const deleteCredential = async (gstin: string) => {
  if (!gstin) throw new Error(GenericError.ERROR);
  const response = (await ApiServices.deleteCredentialAPI(gstin)) as boolean;
  return response;
};

export const toggleCredentialActivation = async (data: GstinCredentialType) => {
  if (!data) throw new Error(GenericError.ERROR);
  const response = (await ApiServices.toggleCredentialActivationAPI(
    data
  )) as GstinCredentialType;
  return response;
};

export const updateFinAccountDetails = async (
  id: any,
  data: any
): Promise<any> => {
  const response = await ApiServices.updateFinanceAccountDetails(id, data);
  return response;
};

export const createVirtualAccount = async (data: any): Promise<any> => {
  const response = await ApiServices.createFinanceVirtualAccount(data);
  return response;
};

export const updatePrimaryVAN = async (data: any): Promise<any> => {
  const response = await ApiServices.updateFinancePrimaryVAN(data);
  return response;
};

export const addAccount = async (data: any): Promise<any> => {
  const response = await ApiServices.addPayoutAccount(data);
  return response;
};

export const uploadManualVerificationDoc = async (
  presignedUrl: string,
  file: UploadFile,
  headers: Record<string, string>
) =>
  fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers,
  });

export const uploadFileToS3 = async (
  url: string,
  file: UploadFile,
  metadata: any
) =>
  fetch(url, {
    method: 'PUT',
    headers: metadata,
    body: file,
  });

export const deleteBankAccountAction = async (id: number) =>
  ApiServices.deletePayoutAccount(id);

export const getCompanyTerms = async (page = 0, size = 10): Promise<any> =>
  ApiServices.fetchCreditTermsForCompany({ page, size });

export const updateCompanyTerms = async (record): Promise<any> =>
  ApiServices.saveCreditTerms(record);

export const searchCompanies = async (name?: string): Promise<any> =>
  ApiServices.getCompaniesByName(name);

export const generateConfigKey = (config) =>
  `${config.vendor_id}-${config.mapping_type}`;

export const makePrimaryPayoutAccountAction = async (
  id: string | number
): Promise<any> => ApiServices.makePrimaryPayoutAccount(id);
